import React, { useCallback, useEffect } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { getLocales as getLocaleOptions, getLocalizations } from '../redux/slices/localizationSlice';
import Loading from '../components/Loading';

const LocalizationContext = React.createContext();

export const useLocalization = () => React.useContext(LocalizationContext);

export function LocalizationProvider({ children }) {
  const state = useSelector((state) => state.localizations);
  const dispatch = useDispatch();

  const getLocales = useCallback(() => {
    dispatch(getLocaleOptions());
  }, []);

  const setLocale = useCallback((locale) => {
    dispatch(getLocalizations(locale));
  }, []);

  useEffect(() => {
    if (!state.options?.length || !state.messages) {
      getLocales();
      setLocale();
    }
  }, []);

  return (
    <IntlProvider
      locale={state.locale || 'fi'}
      messages={state.messages || {}}
      onError={() => null}
    >
      {state.messages && (
        <LocalizationWrapperProvider
          state={state}
          setLocale={setLocale}
        >
          {state.loading && <Loading />}
          {children}

        </LocalizationWrapperProvider>
      )}
    </IntlProvider>
  );
}

function LocalizationWrapperProvider({ state, setLocale, children }) {
  const intl = useIntl();

  const localizeObject = useCallback((obj) => {
    if (typeof obj === 'string') {
      return obj;
    }

    let value = obj?.[state.locale];
    return value || obj?.fi;
  }, [state.locale]);

  const localize = useCallback((id, options = {}) => {
    if (!state.locale) return id;

    if (typeof id !== 'string') {
      return localizeObject(id);
    }

    const { values, ...rest } = options;
    return intl.formatMessage({ id, ...rest }, values);
  }, [state.locale]);

  const format = useCallback((type, value, options = null) => {
    if (!state.locale) return value;
    if (value == null) return '';

    switch (type) {
      case 'language':
        return intl.formatDisplayName(value, { type });
      case 'date':
        return intl.formatDate(new Date(value), options || {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        });
      case 'datetime':
        return intl.formatDate(new Date(value), options || {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric'
        });
      case 'currency':
        return new Intl.NumberFormat(state.locale, {
          ...(options || {
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }),
          style: 'currency'
        }).format(Number(value));
      default:
        return value;
    }
  }, [state.locale]);

  const compare = useCallback((a, b) => {
    return localizeObject(a)?.localeCompare(
      localizeObject(b),
      state.locale,
      { sensitivity: 'base' }
    );
  }, [state.locale]);

  localize.format = useCallback(format, [format]);
  localize.compare = useCallback(compare, [compare]);

  return (
    <LocalizationContext.Provider value={{
      selectedLocale: state.locale,
      locales: state.options,
      messages: state.messages,
      setLocale,
      localize,
      format,
      localizeObject,
      compare
    }}>
      {children}
    </LocalizationContext.Provider>
  );
}
